import { Typography } from "antd";
import React from "react";
import { useTheme } from "../../../ThemeContext";

const { Text } = Typography;

/**
 * CallHistoryTranscript - NEW transcript component for Call History V2
 * Takes transcription data directly as props (no context dependency)
 * Works with new getCallDetails API data structure
 */
export default function CallHistoryTranscript({ transcription, searchInput, currentTime }) {
  const { currentTheme, themeMode } = useTheme();

  // Function to format time from ISO string to HH:MM:SS
  const formatTime = (timeString) => {
    if (!timeString || timeString === "null") return "0:00";

    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (error) {
      console.warn("Error parsing time:", timeString, error);
      return "0:00";
    }
  };

  // Function to get speaker display name and color
  const getSpeakerInfo = React.useCallback((speaker) => {
    const speakerName = speaker || "Unknown";
    const formattedName = speakerName.charAt(0).toUpperCase() + speakerName.slice(1).toLowerCase();

    // Agent/Bot gets orange color, others get default
    const isAgent = ["agent", "bot", "joy"].includes(speakerName.toLowerCase());

    return {
      name: formattedName,
      color: isAgent ? currentTheme.callhistory.orange : currentTheme.callhistory.text,
      isAgent: isAgent,
    };
  }, [currentTheme]);

  // Generate stable unique key for each item
  const getStableKey = (item, index) => {
    if (item._id) return `transcript-${item._id}`;
    const contentHash = (item.utterance || item.rawUtterance || "").slice(0, 20);
    const timeHash = item.startTime || "";
    return `transcript-${index}-${contentHash.replace(/\s+/g, "")}-${timeHash}`;
  };

  // Filter messages based on search input
  const filteredTranscription = React.useMemo(() => {
    if (!transcription || transcription.length === 0) {
      return [];
    }

    if (!searchInput || searchInput.trim() === "") {
      return transcription;
    }

    const searchTerm = searchInput.toLowerCase().trim();

    try {
      const filtered = transcription.filter((item) => {
        const text = item.utterance || item.rawUtterance || "";
        const speaker = item.speaker || "";

        const textMatch = text.toLowerCase().includes(searchTerm);
        const speakerMatch = speaker.toLowerCase().includes(searchTerm);

        return textMatch || speakerMatch;
      });

      return filtered;
    } catch (error) {
      console.error("Error filtering transcription:", error);
      return transcription;
    }
  }, [transcription, searchInput]);

  // Calculate speaker statistics
  const speakerStats = React.useMemo(() => {
    if (!filteredTranscription || filteredTranscription.length === 0) return "";

    try {
      const speakerCounts = {};
      filteredTranscription.forEach((item) => {
        const speaker = item.speaker || "Unknown";
        const speakerName = speaker.charAt(0).toUpperCase() + speaker.slice(1).toLowerCase();
        speakerCounts[speakerName] = (speakerCounts[speakerName] || 0) + 1;
      });

      const total = filteredTranscription.length;
      const speakerPercentages = Object.entries(speakerCounts)
        .map(([speaker, count]) => `${speaker} (${Math.round((count / total) * 100)}%)`)
        .join(", ");

      return speakerPercentages;
    } catch (error) {
      console.error("Error calculating speaker stats:", error);
      return "";
    }
  }, [filteredTranscription]);

  const renderMessage = React.useCallback(
    (item, index) => {
      const speaker = item.speaker || "Unknown";
      const startTime = item.startTime;
      const text = item.utterance || item.rawUtterance || "";

      const speakerInfo = getSpeakerInfo(speaker);
      const formattedTime = formatTime(startTime);
      const stableKey = getStableKey(item, index);

      return (
        <div
          key={stableKey}
          style={{
            marginBottom: "1rem",
            paddingBottom: "0.75rem",
            borderBottom:
              index < filteredTranscription.length - 1
                ? `1px solid ${currentTheme.callhistory.borderColor}`
                : "none",
          }}
        >
          {/* Speaker and Time Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "0.5rem",
              gap: "0.5rem",
            }}
          >
            <Text
              style={{
                color: speakerInfo.color,
                fontWeight: "600",
                fontSize: "0.875rem",
              }}
            >
              {speakerInfo.name}
            </Text>
            <Text
              style={{
                color: currentTheme.callhistory.textLight,
                fontSize: "0.75rem",
              }}
            >
              {formattedTime}
            </Text>
          </div>

          {/* Message Text */}
          <div style={{ paddingLeft: "0" }}>
            <Text
              style={{
                color: currentTheme.callhistory.text,
                fontSize: "0.875rem",
                lineHeight: "1.5",
                fontWeight: "400",
              }}
            >
              {text}
            </Text>
          </div>
        </div>
      );
    },
    [filteredTranscription.length, currentTheme, getSpeakerInfo]
  );

  return (
    <div
      style={{
        width: "100%",
        height: "42vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <hr
        style={{
          border: "none",
          borderTop: `1px solid ${currentTheme.callhistory.borderColor}`,
          margin: "0.0625rem 0",
          flexShrink: 0,
        }}
      />

      {/* Header with Title and Speakers Summary */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "0.25rem",
          flexShrink: 0,
        }}
      >
        <Text
          style={{
            color: currentTheme.callhistory.text,
            fontSize: "1.125rem",
            fontWeight: "600",
          }}
        >
          Call Transcript
        </Text>

        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          {/* Speakers Summary */}
          {filteredTranscription && filteredTranscription.length > 0 && speakerStats && (
            <div
              style={{
                padding: "0.5rem 0.75rem",
                backgroundColor:
                  themeMode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                borderRadius: "0.5rem",
                border: `1px solid ${currentTheme.callhistory.borderColor}`,
              }}
            >
              <Text
                style={{
                  color: currentTheme.callhistory.text,
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  marginRight: "0.5rem",
                }}
              >
                Speakers:
              </Text>
              <Text
                style={{
                  color: currentTheme.callhistory.textLight,
                  fontSize: "0.75rem",
                }}
              >
                {speakerStats}
              </Text>
            </div>
          )}
        </div>
      </div>

      {/* Transcript Messages */}
      <div
        style={{
          flex: 1,
          backgroundColor: currentTheme.callhistory.mainBackground,
          borderRadius: "0.5rem",
          padding: "1rem",
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
          maxHeight: "100%",
          scrollbarWidth: "thin",
          scrollbarColor: `${currentTheme.callhistory.borderColor} ${currentTheme.callhistory.mainBackground}`,
        }}
        className={`transcript-scroll ${themeMode === "light" ? "light-scroll" : "dark-scroll"}`}
      >
        <style jsx>{`
          .transcript-scroll::-webkit-scrollbar {
            width: 0.5rem;
            background: ${currentTheme.callhistory.mainBackground};
          }
          .transcript-scroll::-webkit-scrollbar-track {
            background: ${currentTheme.callhistory.mainBackground};
            border-radius: 0.25rem;
            border: 1px solid ${currentTheme.callhistory.borderColor}20;
          }
          .transcript-scroll::-webkit-scrollbar-thumb {
            background-color: ${currentTheme.callhistory.borderColor};
            border-radius: 0.25rem;
            border: none;
          }
          .transcript-scroll::-webkit-scrollbar-thumb:hover {
            background-color: ${
              themeMode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)"
            };
          }
          .transcript-scroll::-webkit-scrollbar-corner {
            background: ${currentTheme.callhistory.mainBackground};
          }
        `}</style>

        <div style={{ display: "flex", flexDirection: "column" }}>
          {filteredTranscription && filteredTranscription.length > 0 ? (
            filteredTranscription.map((item, index) => renderMessage(item, index))
          ) : (
            <div
              key="empty-state-message"
              style={{
                textAlign: "center",
                padding: "2.5rem 1.25rem",
                color: currentTheme.callhistory.textLight,
              }}
            >
              <Text style={{ color: currentTheme.callhistory.textLight }}>
                {searchInput && searchInput.trim() !== ""
                  ? "No messages found matching your search."
                  : "No transcript available."}
              </Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

